// src/ui/bubbles/camera-bias.ts — Phase 6 D-03 player-bubble camera bias.
//
// When the local player's character starts speaking, lerp the GameScene
// camera follow-offset by P6_CAMERA_BIAS_PX so the bubble stays inside the
// safe area for its lifetime; release on bubble auto-expire.
//
// Phaser owns the lerp via `cameras.main.setFollowOffset(dx, dy)` — D-03
// magnitude only, NPC bubbles do NOT shift the camera (they auto-flip the
// tail in Bubble.tsx instead, per CONTEXT D-03 fallback).
//
// Direction: bias DOWN by default (bubble appears above sprite, camera
// shifts down so the bubble stays within safe area). Refinement based on
// safe-area collision is deferred to P7 POL-06 once playtest data lands.
//
// Idempotent: calling apply twice without release is a no-op on the second
// call (active flag), avoiding a stacked offset. Tests assert this.
//
// DOES NOT call stopFollow — the existing P3 startFollow() chain stays
// active; only setFollowOffset is bumped (PATTERNS lines 376-394).

import { P6_CAMERA_BIAS_PX } from '../../render/audio/constants';
import { getGameSceneRef } from '../../render/PhaserGame';

let active = false;

/**
 * Apply the player-bubble camera bias. Idempotent — second call is a no-op
 * until releaseCameraBias() is invoked. If the GameScene ref is not yet
 * cached (returning null), this is a silent no-op so a bubble that fires
 * during the brief window between mount and 'current-scene-ready' simply
 * does not bias the camera.
 */
export function applyCameraBias(): void {
  if (active) return;
  const scene = getGameSceneRef();
  if (!scene) return;
  // D-03 default: bias DOWN. Bubble renders above the player sprite, so
  // shifting the camera down (positive Y) gives the bubble more headroom
  // toward the top of the viewport. setFollowOffset semantics: subtracted
  // from target position → camera centre = target - (0, +bias) = target
  // shifted UP, viewport effectively shifts DOWN relative to player.
  scene.setCameraBias(0, P6_CAMERA_BIAS_PX);
  active = true;
}

/**
 * Release the bias, returning camera follow-offset Y to neutral. If the
 * GameScene ref has gone away (scene shutdown), clear the active flag
 * anyway so a future apply on a fresh scene works correctly.
 */
export function releaseCameraBias(): void {
  if (!active) return;
  const scene = getGameSceneRef();
  if (!scene) {
    active = false;
    return;
  }
  scene.setCameraBias(0, 0);
  active = false;
}

/** Test-only reset between Vitest specs. Not called from production code. */
export function _resetCameraBiasForTests(): void {
  active = false;
}
